<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    offset-y
    max-height="300px"
    min-width="300px"
    max-width="500px"
    content-class="custom-select-menu"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-bind="attrs"
        v-on="on"
        :label="label"
        outlined
        readonly
        append-icon="mdi-menu-down"
        :clearable="selectedItems.length > 0"
        @click:clear="clearAllSelected"
        hide-details
      >
        <template v-slot:selection>
          <v-chip
            v-for="item in selectedItems"
            :key="item.value"
            small
            close
            @click:close="deselectItem(item)"
            class="mr-1 my-1"
          >
            {{ item.name }}
          </v-chip>
        </template>
      </v-text-field>
    </template>

    <v-card>
      <v-list dense v-if="!hasItems">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="grey--text">No items available</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-expansion-panels v-else accordion multiple>
        <v-expansion-panel
          v-for="groupDef in effectiveGroups"
          :key="groupDef.value"
          :value="openGroups.includes(groupDef.value)"
          @change="toggleGroup(groupDef.value, $event)"
        >
          <v-expansion-panel-header>{{ groupDef.title }}</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-list dense>
              <v-list-item
                v-for="item in itemsByGroup[groupDef.value]"
                :key="item.value"
                @click="toggleItemSelection(item)"
                :class="{ 'v-list-item--active primary--text': isItemSelected(item) }"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="isItemSelected(item)">
                  <v-icon color="primary">mdi-check</v-icon>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="!itemsByGroup[groupDef.value] || itemsByGroup[groupDef.value].length === 0">
                <v-list-item-content>
                  <v-list-item-title class="grey--text caption">No items in this group</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  props: {
    items: {
      type: Array,
      required: true,
      validator: (items) =>
        items.every(
          (item) =>
            typeof item === 'object' &&
            item !== null &&
            'name' in item &&
            'value' in item &&
            'group' in item
        ),
    },
    groups: {
      type: Array,
      default: () => [],
      validator: (groups) =>
        groups.every(
          (group) =>
            typeof group === 'object' &&
            group !== null &&
            'title' in group &&
            'value' in group
        ),
    },
    label: {
      type: String,
      default: 'Select items',
    },
  },
  data() {
    return {
      menu: false,
      selectedItems: [], // Now an array for multiple selections
      openGroups: [],
    };
  },
  computed: {
    itemsByGroup() {
      const grouped = this.items.reduce((acc, item) => {
        if (!acc[item.group]) {
          acc[item.group] = [];
        }
        acc[item.group].push(item);
        return acc;
      }, {});

      // Ensure all defined groups are present, even if empty
      this.groups.forEach(groupDef => {
        if (!grouped[groupDef.value]) {
          grouped[groupDef.value] = [];
        }
      });

      return grouped;
    },
    hasItems() {
      return this.items.length > 0;
    },
    effectiveGroups() {
      // If groups prop is provided, use it to order and title panels.
      // Otherwise, infer from items and sort.
      if (this.groups && this.groups.length > 0) {
        // Filter out groups that have no items, unless you want to show empty panels
        const activeGroupValues = Object.keys(this.itemsByGroup);
        return this.groups.filter(groupDef => activeGroupValues.includes(groupDef.value));
      } else {
        // Infer groups from items if not provided
        const inferredGroups = Object.keys(this.itemsByGroup).map(groupName => ({
          title: groupName,
          value: groupName,
        }));
        return inferredGroups.sort((a, b) => a.title.localeCompare(b.title));
      }
    }
  },
  methods: {
    isItemSelected(item) {
      return this.selectedItems.some(sItem => sItem.value === item.value);
    },
    toggleItemSelection(item) {
      if (this.isItemSelected(item)) {
        this.deselectItem(item);
      } else {
        this.selectedItems.push(item);
      }
      this.emitChanges();
    },
    deselectItem(itemToRemove) {
      this.selectedItems = this.selectedItems.filter(item => item.value !== itemToRemove.value);
      this.emitChanges();
    },
    clearAllSelected() {
      this.selectedItems = [];
      this.emitChanges();
    },
    emitChanges() {
      // Emit array of values for v-model compatibility
      this.$emit('input', this.selectedItems.map(item => item.value));
      // Emit array of full item objects on change
      this.$emit('change', this.selectedItems);
    },
    toggleGroup(groupValue, isOpen) {
      if (isOpen) {
        if (!this.openGroups.includes(groupValue)) {
          this.openGroups.push(groupValue);
        }
      } else {
        this.openGroups = this.openGroups.filter(g => g !== groupValue);
      }
    }
  },
  watch: {
    // Optionally watch `selectedItems` to update `openGroups` if you want
    // groups containing selected items to automatically open.
    // This can be complex if you have many selected items across many groups.
  }
};
</script>

<style scoped>
.custom-select-menu .v-card {
  border-radius: 4px; /* Match Vuetify's default card border-radius */
  box-shadow: 0 5px 5px -3px rgba(0,0,0,.2), 0 8px 10px 1px rgba(0,0,0,.14), 0 3px 14px 2px rgba(0,0,0,.12); /* Match Vuetify's default menu shadow */
}

/* Ensure the text field's content area (where chips go) can grow */
.v-text-field .v-input__control .v-input__slot {
  height: auto !important; /* Allow height to adjust for chips */
  min-height: 56px; /* Vuetify default for outlined text-field */
  align-items: center; /* Vertically align items */
  padding-top: 8px; /* Adjust padding to make space for chips and label */
  padding-bottom: 8px;
}

/* Adjust label position for multi-line input */
.v-text-field.v-text-field--enclosed:not(.v-text-field--single-line).v-text-field--outlined .v-label {
    top: 18px; /* Adjust label top position */
}
.v-text-field.v-text-field--enclosed:not(.v-text-field--single-line).v-text-field--outlined.v-input--is-dirty .v-label,
.v-text-field.v-text-field--enclosed:not(.v-text-field--single-line).v-text-field--outlined.v-input--has-state .v-label {
    transform: translateY(-20px) scale(0.75); /* Float label up */
}
</style>